/**
 * The five features, named as the landing doors name them, and the one place that maps the
 * server's feature tags onto them. Pure: no imports beyond types, so any module may use it.
 */

/** The feature a piece of work came from, named as the landing doors name them. */
export type Feature = "research" | "notice" | "appeal" | "oio" | "oia"

/** The server's feature tags, as they arrive on thread, case, workspace and session rows. */
export type FeatureTag = "research" | "notice_analysis" | "appeal_or_not" | "oio_draft" | "oia_draft"

export const FEATURE_LABEL: Record<Feature, string> = {
  research: "Research",
  notice: "Notice analysis",
  appeal: "Appeal or not",
  oio: "Order",
  oia: "Order draft · OIA",
}

/** The server tag for each feature (what a POST /v2/threads `feature` hint and a row's `feature` carry). */
export const FEATURE_TAG: Record<Feature, FeatureTag> = {
  research: "research",
  notice: "notice_analysis",
  appeal: "appeal_or_not",
  oio: "oio_draft",
  oia: "oia_draft",
}

const FROM_TAG: Record<string, Feature> = Object.fromEntries(
  (Object.keys(FEATURE_TAG) as Feature[]).map((f) => [FEATURE_TAG[f], f]),
) as Record<string, Feature>

/**
 * The feature a row belongs to: the server's tag when it sends one, else the fallback the caller
 * infers from what the row is (a session type, a workspace's lead). Research when nothing says otherwise.
 */
export function featureOf(row: { feature?: string | null; inferred?: Feature; type?: "nira" | "oio" | "oia" }): Feature {
  if (row.feature && FROM_TAG[row.feature]) return FROM_TAG[row.feature]
  if (row.inferred) return row.inferred
  return row.type === "oio" ? "oio" : row.type === "oia" ? "oia" : "research"
}

/** The label a feature is shown under, or null for a tag this client does not know. */
export function featureLabel(tag: string | null | undefined): string | null {
  if (!tag) return null
  const f = FROM_TAG[tag]
  return f ? FEATURE_LABEL[f] : null
}
